/**
 * Captures the browser's install prompt (docs/06-roadmap.md P4-02) so a custom banner can trigger
 * it on demand — the browser only ever fires `beforeinstallprompt` once and expects `.preventDefault()`
 * immediately, so this has to listen from the moment the app mounts, not from whenever the banner
 * component happens to render. Chromium-only: Safari/iOS never fires this event (its install path
 * is the OS's own "Add to Home Screen" share-sheet action, nothing a page can trigger or detect) —
 * Settings supplies manual instructions when the browser does not offer a prompt.
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'finbob:install-prompt-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(
    () =>
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true,
  );
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredEvent(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setInstalled(true);
      setDeferredEvent(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function promptInstall() {
    if (!deferredEvent || installing) return;
    const event = deferredEvent;
    setDeferredEvent(null); // Each browser event is single-use, including rejected prompts.
    setInstalling(true);
    setError('');
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === 'dismissed') dismiss();
      else setInstalled(true);
    } catch {
      setError('The install prompt could not open. Use your browser’s install menu instead.');
    } finally {
      setInstalling(false);
    }
  }

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Per-viewer convenience only — a failed write just means the banner may reappear next
      // visit, not a correctness issue worth surfacing.
    }
  }

  return {
    canInstall: !!deferredEvent && !installed && !dismissed,
    canPrompt: !!deferredEvent && !installed,
    installed,
    installing,
    error,
    promptInstall,
    dismiss,
  };
}
